import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpRow } from 'new-components/DLS/OpRow/OpRow';
import { OpCol } from 'new-components/DLS/OpCol/OpCol';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { useSelectorJs } from 'utils/customHooks';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { heliumReceive } from 'utils/helpers';
import { getOpParams } from 'utils/getOpParams';
import { Loader } from 'components/Loader';

import { toLocalByTimezone } from '../../../../utils/dates';
import {
  getStatusNameById,
  getVisitDateTimeRaw,
  getStatusColor,
} from '../../../../utils/visitorsHelper';
import { DATE_TIME_AM_PM_FORMAT } from '../../../../constants/dates';
import STATUS from '../../../../constants/status';
import { formatFullName, statusKeyMap } from '../../../../utils/utils';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { FormFieldWorkflow, VisitData } from '../../../../types/formFieldTypes';
import {
  fetchInvitationCustomFields,
  VisitorInvitationState,
} from '../../../../store/slices/visitorInvitationSlice';
import { VisitsState } from '../../../../store/slices/visitsSlice';
import { LocationsState } from '../../../../store/slices/locationsSlice';

interface LocationWorkflowResponse {
  data?: Array<{
    SignInWorkflowId?: number;
  }> | null;
}

interface VisitorTypeWorkflowResponse {
  data?: Array<{
    workflow?: {
      SignInWorkflowId?: number;
    };
  }> | null;
}

interface WorkflowFieldsResponse {
  data?: FormFieldWorkflow[] | null;
}

interface VisitDataResponse {
  data?: VisitData[] | null;
}

const DetailsTab = () => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { hasReadPermission: hasVisitorDataRead } =
    useEndpointPermissions('getVisitorData');
  const { hasReadPermission: hasWorkflowFieldsCustomRead } =
    useEndpointPermissions('listAllVisitorInvitationFieldsCustom');

  const globalLocationWorkflow = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocationWorkflow']),
  ) as LocationsState['globalLocationWorkflow'];
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const invitationCustomFields = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationCustomFields']),
  ) as VisitorInvitationState['invitationCustomFields'];

  const [statusColor, setStatusColor] = useState('transparent');
  const [customFieldsWorkflow, setCustomFieldsWorkflow] = useState<
    FormFieldWorkflow[]
  >([]);
  const [visitData, setVisitData] = useState<VisitData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const status = getStatusNameById(
    selectedVisit?.visitStatus.id!,
    selectedVisit?.scheduleStart,
    selectedVisit?.location?.timezone,
  );

  useEffect(() => {
    setStatusColor(getStatusColor(status));
    // eslint-disable-next-line
  }, [selectedVisit]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Guarded on visitorWorkflowFieldsCustomReadScopes because the helium
        // GET locationConfig, describeVisitorTypeWorkflow, and fieldCustom routes
        // reject roles without visitor/config read (e.g. dashboard-only) with a 403.
        if (hasWorkflowFieldsCustomRead) {
          const locationDefaultWorkflow = (await heliumReceive(
            'locationConfig',
            [orgId, selectedVisit?.location?.id],
          )) as LocationWorkflowResponse;
          const locationVisitorTypeWorkflow = (await heliumReceive(
            'describeVisitorTypeWorkflow',
            [orgId, selectedVisit?.location.id, selectedVisit?.visitorType.id],
          )) as VisitorTypeWorkflowResponse;

          const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
          const visitorTypeWorkflowData =
            locationVisitorTypeWorkflow?.data?.[0]?.workflow;
          const visitorTypeSignInWorkflowId =
            visitorTypeWorkflowData?.SignInWorkflowId;

          const signInWorkflowId =
            visitorTypeSignInWorkflowId !== undefined &&
            visitorTypeSignInWorkflowId !== -1
              ? visitorTypeSignInWorkflowId
              : defaultWorkflowData?.SignInWorkflowId;

          const workflowFieldsCustom = (await heliumReceive(
            'getVisitorWorkflowFieldsCustom',
            [orgId, signInWorkflowId],
          )) as WorkflowFieldsResponse;
          setCustomFieldsWorkflow(
            Array.isArray(workflowFieldsCustom?.data)
              ? workflowFieldsCustom.data
              : [],
          );

          dispatch(fetchInvitationCustomFields({ orgId }));
        } else {
          setCustomFieldsWorkflow([]);
        }

        // Guarded on visitorDataReadScopes because the helium GET visitorData
        // route rejects roles without visitor/report read (e.g. dashboard-only)
        // with a 403.
        const visitDataResponse = hasVisitorDataRead
          ? ((await heliumReceive('getVisitorData', [
              orgId,
              selectedVisit?.id,
            ])) as VisitDataResponse)
          : null;
        // const filteredVisitData = visitData.data.filter((visitData: VisitData) => visitData.formField.status === 1);
        setVisitData(
          Array.isArray(visitDataResponse?.data) ? visitDataResponse.data : [],
        );
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [
    orgId,
    globalLocationWorkflow?.SignInWorkflowId,
    invitationCustomFields.length,
    // Depend on the whole selectedVisit so edits made in VisitorsEditModal
    // (which refresh selectedVisit via fetchVisits) re-fetch visitData/custom
    // fields and reflect immediately without reopening the drawer.
    selectedVisit,
    hasVisitorDataRead,
    hasWorkflowFieldsCustomRead,
    dispatch,
  ]);

  // Fallback: location timezone → browser timezone
  const tz =
    selectedVisit?.location?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialValues: Record<string, unknown> = {
    company: selectedVisit?.company,
    visitorType: selectedVisit?.visitorType.name,
    visitStatus: t(statusKeyMap[status] || status),
    dateTime:
      status === 'Pending' || status === 'No Show'
        ? ''
        : toLocalByTimezone(
            getVisitDateTimeRaw(selectedVisit, status),
            tz,
          )?.format(DATE_TIME_AM_PM_FORMAT),
    scheduleStart: selectedVisit?.scheduleStart
      ? toLocalByTimezone(selectedVisit.scheduleStart, tz)?.format(
          DATE_TIME_AM_PM_FORMAT,
        )
      : undefined,
    scheduleEnd: selectedVisit?.scheduleEnd
      ? toLocalByTimezone(selectedVisit.scheduleEnd, tz)?.format(
          DATE_TIME_AM_PM_FORMAT,
        )
      : undefined,
    location: selectedVisit?.location?.name,
    host: formatFullName(
      selectedVisit?.host.firstName || '',
      null,
      selectedVisit?.host.lastName || '',
    ),
    purpose: selectedVisit?.purpose,
    confirmReturnItems:
      selectedVisit?.confirmReturnItems !== null
        ? selectedVisit?.confirmReturnItems === 1
          ? 'Yes'
          : 'No'
        : undefined,
  };

  // Add visitData fields to initialValues
  visitData.forEach((data) => {
    const { formField, value } = data;
    initialValues[formField.id] = value;
  });

  const renderFormFields = () => {
    const isPendingOrNoShow =
      selectedVisit?.visitStatus.id === STATUS.PENDING.id ||
      selectedVisit?.visitStatus.id === 99; // no-show

    const fieldsToRender = isPendingOrNoShow
      ? Array.from(
          new Map(
            [...customFieldsWorkflow, ...invitationCustomFields].map((f) => [
              f.id,
              f,
            ]),
          ).values(),
        )
      : customFieldsWorkflow;

    const customFieldIds = fieldsToRender.map((field) => field.id);

    // Combine all fields: workflow fields + additional visitData fields
    const workflowFieldsWithMetadata = fieldsToRender.map((formField) => ({
      type: 'workflow' as const,
      formField,
      value: undefined,
    }));

    const additionalVisitDataFields = visitData
      .filter((visitField) => !customFieldIds.includes(visitField.formField.id))
      .map((visitField) => ({
        type: 'visitData' as const,
        formField: visitField.formField,
        value: visitField.value,
      }));

    // Merge and sort all fields alphabetically by name
    const allFieldsSorted = [
      ...workflowFieldsWithMetadata,
      ...additionalVisitDataFields,
    ].sort((a, b) =>
      (a.formField.name || '').localeCompare(b.formField.name || ''),
    );

    return (
      <>
        {allFieldsSorted.map((item) => {
          const { formField } = item;

          if (formField.values) {
            const options = formField.values.map((option) => ({
              label: option.display || option.name,
              value: option.value,
            }));

            return (
              <OpForm.Select
                key={formField.id}
                label={formField.name}
                name={formField.id}
                options={options}
              />
            );
          }

          // Use TextAreaInput for workflow fields, Input for visitData fields
          if (item.type === 'workflow') {
            return (
              <OpForm.TextAreaInput
                key={formField.id}
                label={formField.name}
                name={formField.id}
                rows={4}
              />
            );
          }

          return (
            <OpForm.Input
              key={formField.id}
              label={formField.name}
              name={formField.id}
            />
          );
        })}
      </>
    );
  };

  return loading ? (
    <Loader />
  ) : (
    <OpForm
      // Ant Design only applies initialValues on mount, so remount the form
      // when the underlying data changes (e.g. after an edit) to reflect the
      // updated values without reopening the drawer.
      key={JSON.stringify(initialValues)}
      onSubmit={() => {}}
      hasError={false}
      defaultButtons={false}
      initialValues={initialValues}
      isReadOnly
    >
      <OpRow gutter={16}>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.company')}
            name="company"
            readOnly
          />
        </OpCol>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.visitorType')}
            name="visitorType"
            readOnly
          />
        </OpCol>
      </OpRow>

      <OpRow gutter={16}>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.status')}
            name="visitStatus"
            readOnly
            style={{
              backgroundColor: statusColor,
              color: 'white',
              WebkitTextFillColor: 'white',
              opacity: 1,
            }}
          />
        </OpCol>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.dateTime')}
            name="dateTime"
            readOnly
          />
        </OpCol>
      </OpRow>

      <OpRow gutter={16}>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.scheduleStart')}
            name="scheduleStart"
            readOnly
          />
        </OpCol>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.scheduleEnd')}
            name="scheduleEnd"
            readOnly
          />
        </OpCol>
      </OpRow>

      <OpRow gutter={16}>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.location')}
            name="location"
            readOnly
          />
        </OpCol>
        <OpCol span={12}>
          <OpForm.Input
            label={t('visitors.details.host')}
            name="host"
            readOnly
          />
        </OpCol>
      </OpRow>

      <OpForm.TextAreaInput
        label={t('visitors.details.purpose')}
        name="purpose"
        readOnly
        rows={4}
      />

      {(selectedVisit?.confirmReturnItems === 0 ||
        selectedVisit?.confirmReturnItems === 1) && (
        <OpForm.Input
          label={t('visitors.details.confirmReturnItems')}
          name="confirmReturnItems"
          readOnly
        />
      )}
      <OpDivider />
      {renderFormFields()}
      {/* {selectedVisit?.visitStatus.id === STATUS.PENDING.id ? undefined : (renderFormFields())} */}
    </OpForm>
  );
};

export default DetailsTab;
